/* eslint-disable @typescript-eslint/no-dynamic-delete */
/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useEffect,
  useRef,
  RefObject,
  useCallback,
  MutableRefObject,
} from 'react';
import { Draft, produce } from 'immer';
import cloneDeep from 'lodash-es/cloneDeep';

/**
 * Las acciones reciben este objeto como terecer parámetro.
 *
 * Sirve para decidir cuáles serán los keys afectados por la ejecución
 * exitosa de la acción actual, de esta forma se evita la necesidad de
 * iterar sobre cada elemento ejecutando acciones selectoras y
 * haciendo comparación entre la selección actual y la anterior.
 *
 * Si este objeto no es utilizado en una acción, se seguirá el
 * comportamiento por defecto que es recorrer todas las keys del
 * store.
 */
export type TKeysDefine = {
  /**
   * Acepta un método que será llamado con el conjunto de todas las
   * keys y deberá responder con las keys que deben ser afectadas.
   */
  filter: (filterCallback: Parameters<string[]['filter']>[0]) => void;
  /**
   * Agrega keys al array de búsqueda. La primera vez que se llama, se
   * agregan sobre un array vacío.
   */
  push: (...newKeys: string[]) => void;
  /**
   * Acepta un array de keys que serán las que se recorrerán en
   * la propagación del nuevo estado.
   */
  set: (newKeys: string[]) => void;
};

/**
 * Las operaciones son métodos que alteran el estado de alguna manera.
 *
 * Idealmente, cada operación indica qué keys afectó, de forma que no
 * sea necesario realizar comparaciones para saber qué elementos deben
 * ser modificados dentro del store.
 *
 * Cada operación recibe state que es un drat de Immer, de igual manera
 * como lo hacen los reducers de createSlice de Redux-toolkit.
 *
 * El payload es arbitrario.
 *
 * defineKeys es un objeto que contiene distintos métodos que permiten
 * alterar el listado de keys que serán iteradas para notificar del
 * cambio de estado.
 */
type TOperation<State, Payload> = (
  state: Draft<State>,
  payload: Payload,
  defineKeys: TKeysDefine,
) => void;

/**
 * Representa el conjunto de todas las operaciones de un store.
 */
export type TOperations<State> = Record<string, TOperation<State, any>>;

/**
 * Conjunto de elementos que distinguen a cada tipo de elemento del
 * store. Se toma a partir de la definición del objeto distinctors
 * del método create.
 */
type TDistinctions<
  Distinctors extends TDistinctors<any>,
  K extends keyof Distinctors,
> = Parameters<Distinctors[K][0]>[0];

/**
 * Es el hook encargado de registrar los callbacks necesarios a los
 * eventos que corresponda de forma de garantizar que las propiedades
 * se apliquen sobre el elemento que corresponda.
 */
export type TUseProperties<State, Distinctors extends TDistinctors<State>> = <
  RefType,
  K extends keyof Distinctors = keyof Distinctors,
>(
  which: K,
  distinctions: TDistinctions<Distinctors, K>,
  comparator?: (
    prevSelection: TDistinctorProperties,
    newSelection: TDistinctorProperties,
  ) => boolean,
) => RefObject<RefType>;

export type TStateChangeListener<State> = (
  state: State,
  previousState: State,
) => unknown;

/**
 * Permite obtener una copia del estado local
 */
export type TGetState<State> = () => State;

/**
 * Permite registrar un callback que será llamado con cada actualización del
 * estado
 */
export type TRegisterStateChangeListener<State> = (
  callback: TStateChangeListener<State>,
) => () => void;

/**
 * Es un método que dadas las distinciones de un elemento particular
 * construye un string que lo identifica.
 */
type TKeyMaker<
  State,
  Distinctors extends TDistinctors<State> = TDistinctors<State>,
  Key extends keyof Distinctors = keyof Distinctors,
> = (distinctions: TDistinctions<Distinctors, Key>) => string;

/**
 * Es un mapa que contiene un TKeyMaker por cada tipo de elemento.
 */
export type TKeysMaker<State, Distinctors extends TDistinctors<State>> = {
  [key in keyof Distinctors]: TKeyMaker<State, Distinctors, key>;
};

/**
 * Tipo que permite extraer el tipo del payload de una operación.
 */
type TOperationParameter<Operation extends TOperation<any, any>> =
  Parameters<Operation>[1];

/**
 * Representa una acción asociada a una operación. Es el método
 * que es llamado desde fuera del store para ejecutar una operación
 * y actualizar el estado. Como consecuencia de la ejecución de esta
 * acción, el estado será actualizado y se harán las notificaciones
 * y actualización de propiedades que correspondan en los elementos
 * afectados.
 */
type TOperationAction<Payload> = (
  props: Payload extends void ? void : Payload,
) => void;

/**
 * Objeto que contiene todas las acciones de un store.
 */
export type TActionsMap<State, Operations extends TOperations<State>> = {
  [Key in keyof Operations]: TOperationAction<
    TOperationParameter<Operations[Key]>
  >;
};

/**
 * Método que es llamado luego de que se actualizan los elementos
 * pertenecientes a un tipo del store.
 */
type TAfterActionCallback<State> = (
  prevState: State,
  nextState: State,
) => unknown;

export type TClassListComposer = Partial<{
  add: string[];
  remove: string[];
  toggle: string[];
}>;

export type TDatasetComposer = Partial<{
  add: Record<string, any>;
  remove: string[];
}>;

export type TDistinctorProperties = Partial<{
  classList: TClassListComposer;
  dataset: TDatasetComposer;
  etc: Record<string, any>;
  styles: CSSStyleDeclaration;
}>;

/**
 * Objeto que representa un tipo de elemento del store.
 */
type TDistinctor<State, Distinctions extends Record<string, any>> =
  | [
      (distinctions: Distinctions, state: State) => TDistinctorProperties,
      TAfterActionCallback<State>,
    ]
  | [(distinctions: Distinctions, state: State) => TDistinctorProperties];

/**
 * Los distinctors determinan para cada tipo de elemento aceptado,
 * si dado un cambio en el estado del store, este elemento debe ser
 * actualizado o no
 * */
export type TDistinctors<State> = Record<string, TDistinctor<State, any>>;

function setAriaAttributes(
  element: HTMLElement,
  attribute: string,
  value: any,
) {
  element.setAttribute(attribute, value as string);
}

/**
 * Representa los callbacks internos del store utilizados para comunicar
 * acerca del cambio de estado a los elementos correspondientes.
 */
type TInnerStateListener<State> = (currentState: State) => void;

function applyProperties(element: HTMLElement, props: TDistinctorProperties) {
  if (!element || !(element instanceof HTMLElement)) return;

  (props.classList?.add ?? []).forEach((current) => {
    if (current) element.classList.add(current);
  });
  (props.classList?.remove ?? []).forEach((current) => {
    if (current) element.classList.remove(current);
  });
  (props.classList?.toggle ?? []).forEach((current) => {
    if (current) element.classList.toggle(current);
  });

  Object.entries(props.dataset?.add ?? {}).forEach(([key, value]) => {
    element.dataset[key] = value;
  });
  (props.dataset?.remove ?? []).forEach(
    (current) => delete element.dataset[current],
  );

  Object.entries(props.styles ?? {}).forEach(([prop, value]) => {
    (element.style[prop as unknown as number] as any) = value;
  });

  Object.entries(props.etc ?? {}).forEach(([prop, value]) => {
    if (value === undefined) {
      delete (element as Record<string, any>)[prop];
      return;
    }

    const match = prop.match(/^aria-(\w+)$/);
    if (match) {
      setAriaAttributes(element, prop, value);
      return;
    }
    (element as Record<string, any>)[prop] = String(value);
  });
}

/**
 * Falta documentar, a grandes rasgos:
 * 
 * Este store propone el máximo rendimiento: afectar solamente los registros
 * que sean estrictamente necesarios y no renderizar.
 * 
 * Para lograr evitar el renderizado, el estado se guarda en internamente en el
 * store, pero también en el elemento asociado a cada registro. De esta manera,
 * cuando se actualiza el estado, se procesan los keys afectados, se genera el
 * estado para cada uno de ellos de acuerdo a su distintor correspondiente y se
 * actualiza el DOM en el momento. Si por algún motivo, el componente afectado
 * vuelve a renderizar, el hook devuelve el estado actualizado, de forma de
 * evitar pérdida de información.
 * 
 * @returns
 * 
 * Al crear un store se devuelve un array compuesto por 3 elementos en el
 * siguiente orden:
 * 
 * - acciones: Un mapa de métodos correspondientes a las operaciones
 *  definidas. 
 * - useProperties: Un hook utilizado para obtener las propiedades
 *  de un elemento, de acuerdo a su distinción.
 * - getState: Un método que permite obtener el estado actual del store.
 *  
 * @example
 * 
 * 
 *   const [actions, useProperties, getState, registerStateListener] =
 *   createFAsomeStore({
    // Se puede utilizar un estado con estructura arbitraria
    initialState: {
      people: [],
      // El as ... es necesario para que typescript pueda realizar la inferencia
      // correctamente. Siempre que se cree un store se debe seguir la estructura
      // propuesta en este ejemplo
    } as { people: { name: string }[] },
    // Los distinguidores "distinctors" establecen qué tipos de elementos existen
    // dentro del store y qué estado utiliza cada uno de ellos
    distinctors: {
      person: [
        // El tipado asignado al parámetro distinctions será tomado por el store
        // para pasarlo luego al keymaker a la hora de seleccionar un registro del
        // store
        (distinctions: { name: string }, state) => {
          return {
            name: state.people.find(
              (current) => current.name === distinctions.name,
            ),
          };
        },
      ],
    },
    // Los keymakers se encargan de generar claves únicas para cada elemento del
    // store
    keysMakers: {
      person: (distinctions) => {
        // Debe crear una key única dentro del store
        return distinctions.name;
      },
    },
    // Los operadores permiten alterar el estado actual
    operations: {
      addPerson(state, payload: { name: string }, affectedKeys) {
        // El estado se altera de cualquier forma que sea conveniente
        state.people.push({ name: payload.name });
        // Este key debe ser idéntico al generado por el keyMaker de person (Más
        // arriba)
        affectedKeys.set([payload.name]);
      },
    },
  });
 */
export function createFAsomeStore<
  State,
  Distinctors extends TDistinctors<State> = TDistinctors<State>,
  Operations extends TOperations<State> = TOperations<State>,
>({
  initialState,
  operations,
  distinctors,
  keysMakers,
}: {
  initialState: State;
  operations: Operations;
  distinctors: Distinctors;
  keysMakers: TKeysMaker<State, Distinctors>;
}): [
  TActionsMap<State, Operations>,
  TUseProperties<State, Distinctors>,
  TGetState<State>,
  TRegisterStateChangeListener<State>,
] {
  const actions = {} as TActionsMap<State, Operations>;
  let state = initialState;

  const callbacks: Record<string, Map<string, TInnerStateListener<State>>> = {};
  const keysMap = new Map<string, number>();

  function registerCallback<K extends keyof Distinctors>(
    which: K,
    distinctions: TDistinctions<Distinctors, K>,
    callback: TInnerStateListener<State>,
  ) {
    if (!callbacks[which as string]) {
      callbacks[which as string] = new Map();
    }
    const key = keysMakers[which](distinctions);
    keysMap.set(key, (keysMap.get(key) ?? 0) + 1);
    callbacks[which as string].set(key, callback);

    return () => {
      callbacks[which as string].delete(key);
      keysMap.set(key, (keysMap.get(key) as number) - 1);
      if (keysMap.get(key) === 0) keysMap.delete(key);
    };
  }

  let stateListenersCallbacks: TStateChangeListener<State>[] = [];

  const registerStateListener: TRegisterStateChangeListener<State> = (
    callback: TStateChangeListener<State>,
  ) => {
    stateListenersCallbacks.push(callback);

    return () => {
      stateListenersCallbacks = stateListenersCallbacks.filter(
        (current) => current !== callback,
      );
    };
  };

  Object.entries(operations).forEach(([name, operation]) => {
    const action: TOperationAction<TOperationParameter<typeof operation>> = (
      props,
    ) => {
      let keys = Array.from(keysMap.keys()) as string[];
      const pushedKeys: Record<string, any> = {};
      let hasPushed = false;
      const nextState = produce(state, (state) =>
        operations[name](state, props, {
          filter(filterCallback) {
            if (hasPushed) {
              Object.keys(pushedKeys)
                .filter((current, ...e) => filterCallback(current, ...e))
                .forEach((current) => delete pushedKeys[current]);
            } else keys = keys.filter(filterCallback);
          },
          push(...newKeys) {
            if (!hasPushed) {
              hasPushed = true;
            }
            newKeys.forEach((current) => (pushedKeys[current] = true));
          },
          set(newKeys) {
            if (!hasPushed) {
              hasPushed = true;
            }
            newKeys.forEach((current) => (pushedKeys[current] = true));
          },
        }),
      );

      const actualKeys = hasPushed ? Object.keys(pushedKeys) : keys;
      hasPushed ? Object.keys(pushedKeys) : keys;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if ((window as any).debugDomStore) {
        console.clear();
      }
      if (nextState !== state) {
        stateListenersCallbacks.forEach((current) => {
          current(nextState, state);
        });
        Object.keys(callbacks).forEach((which) => {
          actualKeys.forEach((key) => {
            callbacks[which].get(key)?.(nextState);
          });
          distinctors[which][1]?.(state, nextState);
        });
        state = nextState;
      }
    };
    actions[name as keyof TActionsMap<State, Operations>] = action;
  });

  /**
   * Este hook permite recuperar el estado asociado a un elemento. 
   * El siguiente ejemplo está tomado del componente Listbox de 
   * **@apia/components**. En el mismo se puede ver que se toman las 
   * props devueltas y se hacen spread sobre el elemento destino. 
   * En este ejemplo, las acciones y el useListbox vienen por 
   * contexto pero son exactamente los mismos devueltos por 
   * createFAsomeStore.
   * 
   * @example
   * 
   * export const ListboxItem: FC<TListboxItem> = ({
      children,
      rowIndex: outerRowIndex,
    }) => {
      const { listboxActions, useListbox } = useContext(ListboxContext);
      const rowIndex = useMemo(() => outerRowIndex ?? -1, [outerRowIndex]);
      const props = useListbox<HTMLDivElement>('row', { rowIndex });

      return (
        <div
          {...props}
          onClick={useCallback(() => {
            listboxActions.updateSelectedRows({ newSelectedRows: [rowIndex] });
          }, [listboxActions, rowIndex])}
        >
          {children}
        </div>
      );
    };
   */
  const useProperties: TUseProperties<State, Distinctors> = <
    RefType,
    K extends keyof Distinctors = keyof Distinctors,
  >(
    which: K,
    distinctions: TDistinctions<Distinctors, K>,
    comparator: (
      prevSelection: TDistinctorProperties,
      newSelection: TDistinctorProperties,
    ) => boolean = (a, b) => a === b,
  ) => {
    const ref = useRef<RefType>(null);
    const returnRef = useCallback((el: RefType) => {
      if (el) {
        applyProperties(
          el as unknown as HTMLElement,
          previousSelection.current,
        );
      }
      (ref as MutableRefObject<RefType>).current = el;
    }, []) as unknown as MutableRefObject<RefType>;
    const previousSelection = useRef<TDistinctorProperties>(
      null as unknown as TDistinctorProperties,
    );
    if (previousSelection.current === null) {
      previousSelection.current = distinctors[which][0](
        distinctions,
        state,
      ) as TDistinctorProperties;
    }

    useEffect(() => {
      return registerCallback(which, distinctions, (current) => {
        const newState: TDistinctorProperties = distinctors[which][0](
          distinctions,
          current,
        );
        if (!comparator(previousSelection.current, newState)) {
          previousSelection.current = newState;
          applyProperties(ref.current as HTMLElement, newState);
        }
      });
    }, [comparator, distinctions, which]);

    return returnRef;
  };

  const getState = () => {
    return cloneDeep(state);
  };

  return [actions, useProperties, getState, registerStateListener];
}

export type { Draft };
